import { useEffect } from "react";
import { useOrganizationStore } from "@/store/useOrganizationStore";
import { getOragnizationData } from "@/services/OrganizationService";
import { OnboardingProgress } from "@/services/OrganizationService";

export const useOnboardingProgress = () => {
  const {
    setShowOnboardingFlow,
    setOnboardingProgress,
    setCompletedStep1Actions,
    setCompletedStep2Actions,
    setCurrentOrganizationId,
    onboardingRefreshTrigger,
    onboardingProgress,
    currentOrganizationId
  } = useOrganizationStore();

  useEffect(() => {
    const initializeOnboardingProgress = async () => {
      // If we already have onboarding progress and organization ID, don't refetch
      if (onboardingProgress && currentOrganizationId) {
        return;
      }

      try {
        const response = await getOragnizationData();

        // Check if the response has the new onboarding progress format
        if (response && typeof response === "object") {
          const orgData = response as any;

          // Set organization ID first to enable organization-specific localStorage
          if (orgData._id) {
            setCurrentOrganizationId(orgData._id);
          }

          // Set showOnboardingFlow flag (default to true if not present)
          if (
            "showOnboardingFlow" in orgData &&
            typeof orgData.showOnboardingFlow === "boolean"
          ) {
            setShowOnboardingFlow(orgData.showOnboardingFlow);
          } else {
            setShowOnboardingFlow(true);
          }

          // Initialize completed actions - get from organization-specific localStorage
          const existingStep1Actions = new Set<string>();
          const existingStep2Actions = new Set<string>();

          try {
            const orgId = orgData._id;
            if (orgId) {
              const storedStep1 = localStorage.getItem(
                `completedStep1Actions_${orgId}`
              );
              const storedStep2 = localStorage.getItem(
                `completedStep2Actions_${orgId}`
              );
              if (storedStep1) {
                JSON.parse(storedStep1).forEach((id: string) =>
                  existingStep1Actions.add(id)
                );
              }
              if (storedStep2) {
                JSON.parse(storedStep2).forEach((id: string) =>
                  existingStep2Actions.add(id)
                );
              }
            }
          } catch (error) {
            console.warn(
              "Failed to load completed actions from localStorage:",
              error
            );
          }

          // Start with existing completed actions from localStorage
          const completedStep1Actions = new Set(existingStep1Actions);
          const completedStep2Actions = new Set(existingStep2Actions);

          // Set onboarding progress
          if (
            orgData.onboardingProgress &&
            typeof orgData.onboardingProgress === "object"
          ) {
            setOnboardingProgress(
              orgData.onboardingProgress as OnboardingProgress
            );

            // Map progress keys back to action IDs - matching the actual action IDs used in the component
            const progressToActionMap: Record<
              keyof OnboardingProgress,
              { actionId: string; step: "step1" | "step2" }
            > = {
              customer: { actionId: "customer", step: "step1" },
              customersReportOverview: { actionId: "reports", step: "step1" },
              smsCampaigns: { actionId: "sms", step: "step1" },
              productCatalog: { actionId: "products", step: "step2" },
              transactions: { actionId: "transactions", step: "step2" },
              transactionReportOverview: {
                actionId: "transactionReportOverview",
                step: "step2"
              },
              automatedCampaigns: {
                actionId: "automatedCampaigns",
                step: "step2"
              },
              behaviorAndEngagementReport: {
                actionId: "behaviorAndEngagementReport",
                step: "step1"
              },
              purchasePatternReport: {
                actionId: "purchasePatternReport",
                step: "step1"
              },
              salesReportOverview: {
                actionId: "salesReportOverview",
                step: "step2"
              },
              trendsAndPatternsReport: {
                actionId: "trendsAndPatternsReport",
                step: "step2"
              },
              productInsightsReport: {
                actionId: "productInsightsReport",
                step: "step2"
              }
            };

            // Check each progress item and add completed actions from backend
            // This merges backend state with local state instead of replacing it
            Object.entries(orgData.onboardingProgress).forEach(
              ([key, status]) => {
                if (
                  status === "DONE" &&
                  progressToActionMap[key as keyof OnboardingProgress]
                ) {
                  const { actionId, step } =
                    progressToActionMap[key as keyof OnboardingProgress];
                  if (step === "step1") {
                    completedStep1Actions.add(actionId);
                  } else {
                    completedStep2Actions.add(actionId);
                  }
                }
              }
            );

            // Only update if there are changes to avoid unnecessary re-renders
            setCompletedStep1Actions(completedStep1Actions);
            setCompletedStep2Actions(completedStep2Actions);
          } else {
            // Even if API doesn't have progress, load from localStorage
            setCompletedStep1Actions(completedStep1Actions);
            setCompletedStep2Actions(completedStep2Actions);
          }
        } else {
          // Fallback: load from localStorage even if API fails
          // Note: Without organization ID, we can't load organization-specific data
          setCompletedStep1Actions(new Set<string>());
          setCompletedStep2Actions(new Set<string>());
        }
      } catch (error) {
        console.error("❌ Failed to initialize onboarding progress:", error);
      }
    };

    initializeOnboardingProgress();
  }, [onboardingRefreshTrigger]); // Run when refresh trigger changes or component mounts
};
